import React, { FC, useEffect, useState, memo } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType } from 'src/types/table';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { SkladonMappingType } from '../../types/skladonMapping';
import { UserServiceCode } from '../../types/user';
import { dhlSortedCodes } from '../OrderDetailContainer/Transport';
import { OrderType } from '../../types/order';
import translate from '@translate';
import { Box } from '@mui/material';

export type ShippingMethodsEditKeysType =
    | 'UID'
    | 'SHIPPER'
    | 'SHIPPER_SERVICE_CODE'
    | 'NAME'
    | 'IMPORT'
    | 'EXPORT'
    | 'BRANCH_ID';
export type ShippingMethodsType =
    { ID: number, ID_SHIPPER?: number, ID_SHIPPER_SERVICE_CODE?: number, ID_BRANCH_ID?: number, SAVED?: string }
    & Pick<SkladonMappingType, 'UID' | 'NAME' | 'IMPORT'>
    & Pick<OrderType, 'SHIPPER' | 'SHIPPER_SERVICE_CODE' | 'BRANCH_ID'>;
const allColumns: TableColumnType[] = [
    {
        key: 'UID',
        label: 'uid',
        customRender: true
    },
    {
        key: 'NAME',
        label: 'name'
    },
    {
        key: 'SHIPPER',
        label: 'shipper',
        customRender: true
    },
    {
        key: 'SHIPPER_SERVICE_CODE',
        label: 'shipperServiceCode',
        customRender: true
    },
    {
        key: 'BRANCH_ID',
        label: 'branchId',
        customRender: true
    },
    {
        key: 'IMPORT',
        label: 'import',
        type: 'select',
        selectOption: 'boolNumberCodes',
        customRender: true
    },
    {
        key: 'SAVED',
        label: 'saved',
        customRender: true
    }
];

export interface ShippingMethodsTablePropsType {
    data?: SkladonMappingType[] | null;
    boolNumberCodes: ConstantsCodeType[];
    shipperCodes: ConstantsCodeType[];
    shipperServiceCodes: ConstantsCodeType[];
    providerServiceCodes: UserServiceCode[];
    changeValue: (key: ShippingMethodsEditKeysType, value: any, error: boolean, id: number) => void;
    loading: boolean;
}

const ShippingMethodsTable: FC<ShippingMethodsTablePropsType> = (props) => {
    const [shipperCodes, setShipperCodes] = useState<ConstantsCodeType[]>([]);

    useEffect(() => {
        if (props.shipperCodes && props.providerServiceCodes) {
            setShipperCodes(
                props.shipperCodes.filter((shipperCode) =>
                    props.providerServiceCodes.find(
                        (providerServiceCode) => providerServiceCode.SHIPPER_CODE === shipperCode.value
                    )
                )
            );
        }
    }, [props.shipperCodes, props.providerServiceCodes]);
    const getInputName = (id: number, key: string) => `product-${id}-${key}`;

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        let serviceCodesFiltered: ConstantsCodeType[] = [];
        if (row.SHIPPER && shipperCodes) {
            const filteredServiceCodes = props.shipperServiceCodes.filter(
                (serviceCodes) =>
                    serviceCodes.isExpedition &&
                    serviceCodes.shipperCode === row.SHIPPER &&
                    props.providerServiceCodes.find(
                        (providerServiceCode) => providerServiceCode.VALUE === serviceCodes.value
                    )
            );

            // dhl needs different sorting
            if (row.SHIPPER === 'DHL') {
                const newCodes: ConstantsCodeType[] = [];
                dhlSortedCodes.forEach((code) => {
                    const found = filteredServiceCodes.find((filteredCode) => filteredCode.value === code);
                    // push only those that provider has access to
                    if (found) {
                        newCodes.push(found);
                    }
                });
                serviceCodesFiltered = newCodes;
            } else {
                serviceCodesFiltered = filteredServiceCodes;
            }
        }
        switch (column.key) {
            case 'SHIPPER':
                return (
                    <Select
                        options={props.shipperCodes}
                        editableOptions={shipperCodes}
                        value={row.SHIPPER}
                        onChange={(value, error) => {
                            props.changeValue('SHIPPER', value, error, row.ID);
                        }}
                        name={name}
                        fullWidth
                    />
                );
            case 'SHIPPER_SERVICE_CODE':
                return (
                    <Select
                        options={props.shipperServiceCodes}
                        editableOptions={serviceCodesFiltered}
                        value={row.SHIPPER_SERVICE_CODE}
                        onChange={(value, error) => props.changeValue('SHIPPER_SERVICE_CODE', value, error, row.ID)}
                        name={name}
                        fullWidth
                    />
                );
            case 'BRANCH_ID':
                return (
                    <TextField
                        value={row.BRANCH_ID ?? ''}
                        onChange={(value, error) => props.changeValue('BRANCH_ID', value ?? '', error, row.ID)}
                        name={name}
                        fullWidth
                        inputProps={{ 'data-testid': `integration-shipping-branch-id-${row.ID}` }}
                    />
                );
            case 'IMPORT':
                return (
                    <Select
                        options={props.boolNumberCodes}
                        value={row.IMPORT ?? 1}
                        onChange={(value, error) => props.changeValue('IMPORT', value, error, row.ID)}
                        name={name}
                        fullWidth
                    />
                );
            case 'SAVED':
                const isSaved = (row.ID_SHIPPER && row.ID_SHIPPER > 0) || (row.ID_SHIPPER_SERVICE_CODE && row.ID_SHIPPER_SERVICE_CODE > 0);
                return (
                    <Box
                        sx={{
                            color: isSaved ? 'green' : 'red',
                            fontWeight: 'bold'
                        }}
                    >
                        {isSaved
                            ? translate('integration.mappingTable.saved.yes')
                            : translate('integration.mappingTable.saved.notSaved')
                        }
                    </Box>
                );
            default:
                return null;
        }
    };

    const getRowSx = (row: any) => {
        const isSaved = (row.ID_SHIPPER && row.ID_SHIPPER > 0) || (row.ID_SHIPPER_SERVICE_CODE && row.ID_SHIPPER_SERVICE_CODE > 0);
        if (!isSaved) {
            return {
                backgroundColor: '#ffebee' // Light red background for unsaved rows
            };
        }
        return {};
    };

    return (
        <TableSimple
            dataTestId="integration-shipping-methods"
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="integration.shippingMethodsTable"
            allColumns={allColumns}
            initSort={{ column: 'UID', direction: 'ASC', createIdNumber: true }}
            selectOptions={{
                boolNumberCodes: props.boolNumberCodes
            }}
            customRender={customRender}
            rowSxFce={getRowSx}
            disableFilter
            maxHeight={'unset'}
            defaultItems={100}
            loading={props.loading}
        />
    );
};

export default memo(ShippingMethodsTable);
